import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Briefcase, Users, ShieldCheck, BarChart2, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";

const NavPill = ({ to, href, children }) => {
  if (href) {
    return (
      <a href={href} className="text-white/80 hover:text-white text-sm font-medium transition-colors" data-testid={`nav-${children.toString().toLowerCase()}`}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className="text-white/80 hover:text-white text-sm font-medium transition-colors" data-testid={`nav-${children.toString().toLowerCase()}`}>
      {children}
    </Link>
  );
};

const Feature = ({ icon: Icon, title, desc, tag, delay }) => (
  <Card className={`card-lift border-0 shadow-sm rounded-xl fade-up ${delay}`} data-testid={`feature-${title.toLowerCase().replace(/\s+/g,'-')}`}>
    <CardContent className="p-8">
      <div className="w-11 h-11 rounded-lg grid place-items-center mb-5" style={{ backgroundColor: "#FFE0F0" }}>
        <Icon className="w-5 h-5 text-pink" />
      </div>
      <Badge variant="outline" className="mb-3 text-[10px] uppercase tracking-wider border-neutral-300 text-neutral-500">{tag}</Badge>
      <h3 className="text-lg font-semibold text-neutral-900 mb-2">{title}</h3>
      <p className="text-sm text-neutral-600 leading-relaxed">{desc}</p>
    </CardContent>
  </Card>
);

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="hero-gradient relative overflow-hidden">
        <div className="grain" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src="/alfred-ai-white.png" alt="alfred ai" className="h-9 w-auto" />
            <span className="text-white/50 text-xs uppercase tracking-widest border-l border-white/20 pl-3 hidden sm:inline">HR</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <NavPill href="#product">Product</NavPill>
            <NavPill href="#pricing">Pricing</NavPill>
            <NavPill href="#compliance">Compliance</NavPill>
            <NavPill to="/quickstart">Docs</NavPill>
          </nav>
          <div className="flex items-center gap-3">
            <a href="https://new.alfredai.bot/book" target="_blank" rel="noopener noreferrer" data-testid="landing-book-call-link" className="hidden sm:inline text-white/85 hover:text-white text-sm font-medium">Book a call</a>
            <Link to="/login" data-testid="landing-signin-link" className="text-white/85 hover:text-white text-sm font-medium">Sign in</Link>
            <Link to="/signup" data-testid="landing-start-trial-btn">
              <Button className="btn-primary rounded-full px-5 focus-ring">Start free trial</Button>
            </Link>
          </div>
        </div>

        {/* Hero */}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <Badge className="rounded-full px-3 py-1 mb-6 border-0" style={{ backgroundColor: "rgba(255,255,255,0.12)", color: "white" }}>
              <span className="mr-2 inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#0CDC73" }} /> ISO 27001 · GDPR · SOC 2 Type II Compliant &nbsp;·&nbsp; ISO 42001 &amp; ISO 45001 Ready
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
              Hire, onboard and <span className="text-pink">stay compliant</span> — all in one place.
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
              alfredHR is the AI‑native HR platform for modern teams. Video interviews, weighted scorecards, automated onboarding, and continuous compliance monitoring — with real‑time evidence for auditors.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/signup" data-testid="hero-cta-primary">
                <Button className="btn-primary rounded-full px-7 py-6 text-base focus-ring">
                  Start free trial <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-cta-secondary">
                <Button variant="ghost" className="text-white hover:bg-white/10 rounded-full px-7 py-6 text-base">
                  Sign in to demo
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-white/70 text-sm">
              {["Blind evaluation", "AI transcripts", "Kanban pipeline", "Certification wallet"].map((t) => (
                <div key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-teal" /> {t}</div>
              ))}
            </div>
          </div>

          {/* Preview card */}
          <div className="lg:col-span-5 fade-up delay-2">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl blur-2xl" style={{ background: "radial-gradient(circle, rgba(255,0,127,0.25), transparent 60%)" }} />
              <Card className="relative rounded-2xl overflow-hidden border-0 shadow-2xl">
                <div className="p-4 border-b flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  <span className="ml-3 text-xs text-neutral-500 font-mono">alfred.app/recruit/jobs/sr-engineer</span>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-neutral-500">Pipeline</div>
                      <div className="text-lg font-semibold">Senior Full‑Stack Engineer</div>
                    </div>
                    <Badge className="bg-teal text-white border-0">Live</Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: "Invited", n: 12, c: "bg-neutral-200" },
                      { label: "Submitted", n: 7, c: "bg-amber-200" },
                      { label: "Evaluated", n: 4, c: "bg-emerald-200" },
                      { label: "Offered", n: 1, c: "bg-pink-200" },
                    ].map((s) => (
                      <div key={s.label} className="rounded-lg bg-neutral-50 p-3">
                        <div className={`h-1.5 rounded-full ${s.c} mb-2`} />
                        <div className="text-[10px] uppercase text-neutral-500">{s.label}</div>
                        <div className="text-lg font-bold">{s.n}</div>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 space-y-2">
                    {[
                      { n: "Ava Thompson", s: 92, r: "Strong Yes" },
                      { n: "Ben Nakamura", s: 78, r: "Yes" },
                      { n: "Chloe Reyes", s: 64, r: "No" },
                    ].map((c) => (
                      <div key={c.n} className="flex items-center justify-between text-sm p-2 rounded-md hover:bg-neutral-50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-navy text-white grid place-items-center text-xs font-semibold">{c.n.split(" ").map(x=>x[0]).join("")}</div>
                          <div>
                            <div className="font-medium">{c.n}</div>
                            <div className="text-xs text-neutral-500">{c.r}</div>
                          </div>
                        </div>
                        <div className="font-mono text-sm font-bold text-pink">{c.s}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </header>

      {/* Logo bar */}
      <section className="border-b py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center text-xs uppercase tracking-widest text-neutral-500 mb-6">Trusted by growing teams</div>
          <div className="flex flex-wrap items-center justify-center gap-x-14 gap-y-4 opacity-70">
            {["Northwind", "Contoso", "Fabrikam", "Umbrella", "Wayne HR", "Stark People"].map((l) => (
              <span key={l} className="font-semibold text-neutral-500 tracking-tight">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="product" className="py-24 bg-white scroll-mt-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <Badge variant="outline" className="mb-4">Platform</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900">One platform. Every stage of the employee lifecycle.</h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">From the first invitation to the exit interview, Alfred keeps HR moving — with AI where it helps and audit trails everywhere.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Feature icon={Briefcase} title="Recruit" tag="Module 1" delay="delay-1"
              desc="AI‑generated job descriptions, video interviews, weighted scorecards, and a drag‑and‑drop Kanban pipeline." />
            <Feature icon={Users} title="HR & People" tag="Module 2" delay="delay-2"
              desc="Employee digital files, org chart, leave, timesheets, and payroll sync to Xero and Employment Hero." />
            <Feature icon={CheckCircle2} title="Onboarding" tag="Module 3" delay="delay-3"
              desc="Templated task lists, e‑signature contracts, automated IT provisioning, and offboarding mirrors." />
            <Feature icon={ShieldCheck} title="Compliance" tag="Module 4" delay="delay-4"
              desc="Continuous monitors for ISO 27001, ISO 42001 and SOC 2. Certification wallet with AI OCR." />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24" style={{ backgroundColor: "#F5F5F5" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="outline" className="mb-4">How it works</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Post a job. Screen at scale. Onboard on autopilot.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Post & Invite", d: "Draft a role with AI JD generation and invite candidates to a one‑way video interview." },
              { n: "02", t: "Screen & Score", d: "Watch responses, use weighted scorecards, and let AI summarise transcripts by competency." },
              { n: "03", t: "Hire & Onboard", d: "Move to Hired to auto‑create the employee, launch onboarding, and provision IT accounts." },
            ].map((s, i) => (
              <Card key={s.n} className={`border-0 shadow-sm rounded-xl fade-up delay-${i+1}`}>
                <CardContent className="p-8">
                  <div className="text-pink font-mono text-sm mb-3">{s.n}</div>
                  <h3 className="text-xl font-semibold mb-2">{s.t}</h3>
                  <p className="text-neutral-600 leading-relaxed">{s.d}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-white scroll-mt-24" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="outline" className="mb-4">Pricing</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Simple, transparent pricing</h2>
            <p className="mt-4 text-neutral-600">Flat monthly fee + usage‑based AI credits. No candidate limits on the top tier.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Starter", price: "$149", pool: "100 active candidates", credits: "500 AI credits/mo", cta: "Start free trial" },
              { name: "Growth", price: "$499", pool: "500 active candidates", credits: "2,000 AI credits/mo", cta: "Start free trial", featured: true },
              { name: "Enterprise", price: "Custom", pool: "Unlimited candidates", credits: "Custom AI credits", cta: "Talk to sales", href: "https://new.alfredai.bot/book" },
            ].map((p) => (
              <Card key={p.name} className={`rounded-2xl border-0 shadow-sm ${p.featured ? "ring-2 ring-pink" : ""}`}>
                <CardContent className="p-8">
                  {p.featured && <Badge className="bg-pink text-white border-0 mb-3">Most popular</Badge>}
                  <div className="text-sm uppercase tracking-wider text-neutral-500 mb-2">{p.name}</div>
                  <div className="text-4xl font-bold mb-1">{p.price}<span className="text-base font-normal text-neutral-500">{p.price !== "Custom" ? "/mo" : ""}</span></div>
                  <ul className="mt-6 space-y-3 text-sm">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-teal mt-0.5" /> {p.pool}</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-teal mt-0.5" /> {p.credits}</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-teal mt-0.5" /> All modules included</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-teal mt-0.5" /> Audit‑ready compliance</li>
                  </ul>
                  <Link to="/signup" className="block mt-6" data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                    <Button className={`w-full rounded-full ${p.featured ? "btn-primary" : "bg-neutral-900 text-white hover:bg-neutral-800"}`}>{p.cta}</Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section id="compliance" className="py-24 bg-neutral-50 scroll-mt-24" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Compliance</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Audit‑ready from day one.</h2>
              <p className="mt-4 text-neutral-600 leading-relaxed">
                Continuous monitors, immutable evidence trails, and one‑click exports auditors accept without follow‑up questions.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-neutral-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-teal mt-0.5" /> <span><strong>ISO 27001 · GDPR · SOC 2 Type II</strong> Compliant</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-teal mt-0.5" /> <strong>ISO 42001</strong> (AI Management) Ready</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-teal mt-0.5" /> <strong>ISO 45001</strong> (Occupational Health &amp; Safety) Ready</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-teal mt-0.5" /> AI OCR certification wallet + expiry alerts</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-teal mt-0.5" /> Vanta‑style evidence pack — one‑click auditor export</li>
              </ul>
            </div>
            <Card className="border-0 shadow-xl rounded-2xl">
              <CardContent className="p-8">
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Health score · Live</div>
                <div className="flex items-baseline gap-3">
                  <div className="text-6xl font-bold font-display">94</div>
                  <div className="text-neutral-500">/ 100</div>
                </div>
                <div className="mt-6 space-y-3">
                  {[
                    { label: "ISO 27001", value: 96, color: "#0CDC73" },
                    { label: "ISO 42001", value: 92, color: "#0CDC73" },
                    { label: "ISO 45001", value: 88, color: "#F6AD55" },
                    { label: "SOC 2 Type II", value: 95, color: "#0CDC73" },
                    { label: "GDPR", value: 97, color: "#0CDC73" },
                  ].map(f => (
                    <div key={f.label}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium">{f.label}</span>
                        <span className="font-mono text-neutral-500">{f.value}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${f.value}%`, backgroundColor: f.color }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 text-[11px] text-neutral-500">Auto‑refreshed every 30 minutes from continuous monitors.</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Docs */}
      <section id="docs" className="py-24 bg-white scroll-mt-24" data-testid="docs-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <Badge variant="outline" className="mb-4">Docs &amp; support</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Everything you need to get up and running.</h2>
          <p className="mt-4 text-neutral-600">From your first job post to your first audit — clear guides, honest limits, human support.</p>
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {[
              { t: "Quickstart", d: "Get your team recruiting in under 15 minutes.", cta: "Read guide", href: "/quickstart" },
              { t: "API reference", d: "Every endpoint, every event, every webhook.", cta: "Open API docs", href: "mailto:hello@alfred.ai?subject=alfredHR%20API%20Docs" },
              { t: "Talk to a human", d: "Real founders answering your questions.", cta: "Book a call", href: "https://new.alfredai.bot/book" },
            ].map(d => (
              <Card key={d.t} className="border-0 shadow-sm rounded-xl text-left card-lift">
                <CardContent className="p-6">
                  <h3 className="font-semibold">{d.t}</h3>
                  <p className="text-sm text-neutral-600 mt-1">{d.d}</p>
                  {d.href.startsWith("/") ? (
                    <Link to={d.href} className="mt-4 inline-flex items-center text-sm font-semibold text-pink hover:underline" data-testid={`docs-cta-${d.t.toLowerCase().replace(/\s+/g,'-')}`}>
                      {d.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Link>
                  ) : (
                    <a href={d.href} target={d.href.startsWith("http") ? "_blank" : undefined} rel={d.href.startsWith("http") ? "noopener noreferrer" : undefined} className="mt-4 inline-flex items-center text-sm font-semibold text-pink hover:underline" data-testid={`docs-cta-${d.t.toLowerCase().replace(/\s+/g,'-')}`}>
                      {d.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-10 bg-navy text-white/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Logo variant="mark-light" size={22} />
            <span className="font-semibold text-white">alfredHR</span>
            <span className="ml-3">© 2026 · alfredHR</span>
          </div>
          <div className="flex items-center gap-6">
            <span>Privacy</span><span>Terms</span><span>Security</span>
            <span className="text-white/50">ISO 27001 · GDPR · SOC 2 Type II</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
